import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { PDFDocument, PDFFont, PDFPage, rgb, StandardFonts } from 'pdf-lib';
import { drawTrialAgreementScopeText, TRIAL_SCOPE_PLACEMENT } from '../utils/trialContractScopePdf';
import {
  SIGNATURE_PAGE_INDEX,
  TEXT_PLACEMENTS as SERVICE_TEXT_PLACEMENTS,
} from '../utils/serviceAgreementLayout';
import {
  CATALOG_LINES_PAGE_INDEX,
  SECOND_CLIENT_SIGNATURE_PLACEMENTS,
  clientInitialsPlacement,
  drawCatalogLines,
  drawPage1Extras,
  drawClientAuthorizationName,
  drawSecondClientSignatureBox,
  insertContractRevisionPages,
  parseAddendums,
  parseCustomProducts,
  planContractExtras,
  shiftPageIndex,
  type ContractCustomProduct,
} from '../utils/contractExtrasPdf';
import { parseUnitPrices } from '../utils/contractPricing';
import './Page.css';
import './Contract.css';

/** Express API base. Defaults to 3001 (the actual server port) so local dev works without REACT_APP_API_URL; the CRA dev server sits on 3000 and would 404 on POSTs. */
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

interface ContractFormData {
  businessName: string;
  serviceAddress: string;
  cityStateZip: string;
  locationContactNamePhone: string;
  locationContactEmail: string;
  authorizedPersonName: string;
  authorizedPersonTitle: string;
  authorizedPersonEmail: string;
  authorizedPersonPhone: string;
  effectiveDate: string;
  termStartDate: string;
  /** New field on the E-Series Service Agreement (e.g. "36 months"). Optional for back-compat. */
  agreementTermLength?: string;
  implementationCost?: string;
  shippingFee?: string;
  qtyTimEBot?: string;
  qtyTimECharger?: string;
  qtyBaseMetalMonthly?: string;
  qtyInsulatedFoodTransportMonthly?: string;
  qtyWheeledBinMonthly?: string;
  qtyUniversalPlatformMonthly?: string;
  qtyDoorOpenersMonthly?: string;
  qtyNeuralTechBrainMonthly?: string;
  qtyElevatorHardwareMonthly?: string;
  qtyLuggageCartMonthly?: string;
  qtyKebbi?: string;
  monthlyRoboticServiceCost?: string;
  qtyBIME?: string;
  qtyBaseMetalOneTime?: string;
  qtyInsulatedFoodTransportOneTime?: string;
  qtyWheeledBinOneTime?: string;
  qtyUniversalPlatformOneTime?: string;
  qtyPlasticBags?: string;
  qtyDoorOpenerHardwareOneTime?: string;
  qtyHandheldTablet?: string;
  additionalAccessoriesCost?: string;
  totalMonthlyCost?: string;
  implementationCostDue?: string;
  techForceSignature?: string;
  scopeText?: string;
  qtyBinsTrial?: string;
  qtyBasesTrial?: string;
  /** Products added beyond the pre-printed catalog lines; may arrive as JSON from the API. */
  customProducts?: ContractCustomProduct[] | string;
  /** Prices negotiated for this contract's catalog lines; may arrive as JSON from the API. */
  unitPrices?: Record<string, number> | string;
  /** Revisions: extra clauses printed ahead of the signature page. */
  addendums?: string[] | string;
  /** Revisions: the client is asked for a second signature in the Authorization section. */
  additionalSignatureRequested?: boolean | string;
}

/** `additionalSignatureRequested` survives a JSONB round-trip as either a boolean or a string. */
function isTruthyFlag(value: unknown): boolean {
  return value === true || value === 'true' || value === 1 || value === '1';
}

type Placement = { x: number; y: number; fontSize?: number; width?: number; height?: number; pageIndex?: number };

/**
 * Service & Trial agreement (E-Series) field placements both come from the
 * shared layout module. The Trial Agreement template is now structurally
 * identical to the Service Agreement (only the title differs), so they use the
 * exact same coordinates. See `src/utils/serviceAgreementLayout.mjs`.
 */
const TEXT_PLACEMENTS: Record<string, Placement> = SERVICE_TEXT_PLACEMENTS;
const TRIAL_TEXT_PLACEMENTS: Record<string, Placement> = SERVICE_TEXT_PLACEMENTS;

/**
 * One drawable signature canvas. The page can show two of these (the client
 * signature and, when the agreement was generated with additional signatures,
 * a second signatory), so the pad state lives in a hook rather than in the
 * component body.
 */
function useSignaturePad() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawingRef = useRef(false);
  const [dataUrl, setDataUrl] = useState<string | null>(null);

  const contextFor = (canvas: HTMLCanvasElement): CanvasRenderingContext2D | null => {
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    // Configured here rather than in an effect: the second pad mounts later.
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    return ctx;
  };

  const pointFor = (
    canvas: HTMLCanvasElement,
    e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>
  ) => {
    const rect = canvas.getBoundingClientRect();
    return {
      x: 'touches' in e ? e.touches[0].clientX - rect.left : e.clientX - rect.left,
      y: 'touches' in e ? e.touches[0].clientY - rect.top : e.clientY - rect.top,
    };
  };

  const startDrawing = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = contextFor(canvas);
      if (!ctx) return;
      isDrawingRef.current = true;
      const { x, y } = pointFor(canvas, e);
      ctx.beginPath();
      ctx.moveTo(x, y);
    },
    []
  );

  const draw = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
      if (!isDrawingRef.current) return;
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      const { x, y } = pointFor(canvas, e);
      ctx.lineTo(x, y);
      ctx.stroke();
    },
    []
  );

  const stopDrawing = useCallback(() => {
    isDrawingRef.current = false;
  }, []);

  const clear = useCallback(() => {
    const canvas = canvasRef.current;
    if (canvas) canvas.getContext('2d')?.clearRect(0, 0, canvas.width, canvas.height);
    setDataUrl(null);
  }, []);

  /** Freeze what has been drawn into a PNG data URL. */
  const capture = useCallback((): string | null => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const url = canvas.toDataURL('image/png');
    setDataUrl(url);
    return url;
  }, []);

  const handlers = {
    onMouseDown: startDrawing,
    onMouseMove: draw,
    onMouseUp: stopDrawing,
    onMouseLeave: stopDrawing,
    onTouchStart: startDrawing,
    onTouchMove: draw,
    onTouchEnd: stopDrawing,
  };

  return { canvasRef, dataUrl, handlers, clear, capture };
}

interface PreparedContractPdf {
  pdfDoc: PDFDocument;
  pages: PDFPage[];
  font: PDFFont;
  boldFont: PDFFont;
  /** Pages inserted for addendums; the signature page moves down by this many. */
  insertedPages: number;
  /** Resolve a template page index to the page it now lives on. */
  pageAt: (templateIndex: number) => PDFPage | undefined;
}

/**
 * Load the template, insert any addendum pages, and draw the contract extras
 * (the page 1 list additions and paragraphs, empty second signature box) that
 * are not pre-printed. Shared by the read-only preview and the signed-PDF build.
 */
async function prepareContractPdf(
  pdfUrl: string,
  formData: ContractFormData
): Promise<PreparedContractPdf> {
  // Revalidate: nginx serves the template without Cache-Control, and an older
  // cached copy would print text that is now drawn at fill time.
  const existingPdfBytes = await fetch(pdfUrl, { cache: 'no-cache' }).then((res) => res.arrayBuffer());
  const pdfDoc = await PDFDocument.load(existingPdfBytes);
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

  // Prices the deal negotiated; anything not overridden charges the catalog default.
  const unitPrices = parseUnitPrices(formData.unitPrices);
  const extrasPlan = planContractExtras(
    {
      quantities: formData,
      unitPrices,
      customProducts: parseCustomProducts(formData.customProducts),
      addendums: parseAddendums(formData.addendums),
    },
    font
  );
  const insertedPages = insertContractRevisionPages(pdfDoc, extrasPlan, font, boldFont);

  const pages = pdfDoc.getPages();
  const pageAt = (templateIndex: number) => pages[shiftPageIndex(templateIndex, insertedPages)];

  const catalogPage = pageAt(CATALOG_LINES_PAGE_INDEX);
  if (catalogPage) {
    drawCatalogLines(catalogPage, unitPrices, font);
    drawPage1Extras(catalogPage, extrasPlan, font, boldFont);
  }

  const signaturePage = pageAt(SIGNATURE_PAGE_INDEX);
  if (signaturePage) {
    drawClientAuthorizationName(signaturePage, boldFont, formData.businessName);
    if (isTruthyFlag(formData.additionalSignatureRequested)) {
      drawSecondClientSignatureBox(signaturePage, font, boldFont, formData.businessName);
    }
  }

  return { pdfDoc, pages, font, boldFont, insertedPages, pageAt };
}

const Contract: React.FC = () => {
  const { contractId } = useParams<{ contractId: string }>();
  const [searchParams] = useSearchParams();
  const [contractType, setContractType] = useState<'service' | 'trial'>(() =>
    searchParams.get('type') === 'service' ? 'service' : 'trial'
  );
  const pdfUrl = contractType === 'service' ? '/agreementLocation.pdf' : '/trialAgreementLocation.pdf';

  const [formData, setFormData] = useState<ContractFormData | null>(null);
  const [filledPdfUrl, setFilledPdfUrl] = useState<string | null>(null);
  /** The PDF saved when the contract was generated, including any direct edits. */
  const [storedGeneratedPdf, setStoredGeneratedPdf] = useState<ArrayBuffer | null>(null);
  const [storedPdfLookup, setStoredPdfLookup] = useState<'loading' | 'ready' | 'missing'>('loading');
  const clientPad = useSignaturePad();
  const signature = clientPad.dataUrl;
  /** Authorization block: the client confirms who is signing, same as the second signatory. */
  const [clientSignerName, setClientSignerName] = useState('');
  const [clientSignerPosition, setClientSignerPosition] = useState('');
  /** Second signatory, requested by the team in the contract's Revisions section. */
  const secondPad = useSignaturePad();
  const [secondSignerName, setSecondSignerName] = useState('');
  const [secondSignerPosition, setSecondSignerPosition] = useState('');
  const [signedPdfUrl, setSignedPdfUrl] = useState<string | null>(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [fillError, setFillError] = useState<string | null>(null);
  const [billingEntity, setBillingEntity] = useState('');
  const [billingAddress, setBillingAddress] = useState('');
  const [billingCity, setBillingCity] = useState('');
  const [billingState, setBillingState] = useState('');
  const [billingZip, setBillingZip] = useState('');
  const [billingContactName, setBillingContactName] = useState('');
  const [billingContactPhone, setBillingContactPhone] = useState('');
  const [billingContactEmail, setBillingContactEmail] = useState('');
  const [contractStatus, setContractStatus] = useState<'pending' | 'signed' | 'void' | null>(null);
  const [contractStatusLoading, setContractStatusLoading] = useState(true);
  const [clientInitials, setClientInitials] = useState('');

  /** True when the agreement was generated asking the client for a second signature. */
  const requiresSecondSignature = isTruthyFlag(formData?.additionalSignatureRequested);

  /** Load form data: legacy links use URL hash; new short links fetch from API after contract is saved. */
  const [formDataLoadState, setFormDataLoadState] = useState<'loading' | 'ready' | 'error'>('loading');

  useEffect(() => {
    const hash = window.location.hash.slice(1);
    if (hash) {
      try {
        const decoded = decodeURIComponent(escape(atob(hash)));
        const data = JSON.parse(decoded) as ContractFormData;
        setFormData(data);
        setContractType(searchParams.get('type') === 'service' ? 'service' : 'trial');
        setFormDataLoadState('ready');
      } catch {
        setFillError('Invalid contract data in link.');
        setFormDataLoadState('error');
      }
      return;
    }
    if (!contractId) {
      setFillError('Invalid contract link.');
      setFormDataLoadState('error');
      return;
    }
    let cancelled = false;
    fetch(`${API_BASE}/api/contracts/by-contract-id/${encodeURIComponent(contractId)}/form-data`)
      .then((res) => {
        if (res.status === 404) throw new Error('notfound');
        if (!res.ok) throw new Error('fail');
        return res.json();
      })
      .then((data: { form_data?: ContractFormData; contract_type?: string }) => {
        if (cancelled) return;
        if (data.form_data) setFormData(data.form_data);
        setContractType(data.contract_type === 'trial' ? 'trial' : 'service');
        setFormDataLoadState('ready');
      })
      .catch(() => {
        if (!cancelled) {
          setFillError('Could not load contract. The link may be invalid or the contract may not be saved yet.');
          setFormDataLoadState('error');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [contractId, searchParams]);

  // Prefill the signer fields with whoever the team listed as the authorized
  // person. The client can correct them before signing.
  useEffect(() => {
    if (!formData) return;
    setClientSignerName((prev) => prev || formData.authorizedPersonName || '');
    setClientSignerPosition((prev) => prev || formData.authorizedPersonTitle || '');
  }, [formData]);

  // Check if contract was already submitted (signed) — show thank-you instead of form
  useEffect(() => {
    if (!contractId) {
      setContractStatusLoading(false);
      setContractStatus('pending');
      return;
    }
    let cancelled = false;
    fetch(`${API_BASE}/api/contracts/status/${encodeURIComponent(contractId)}`)
      .then((res) => (res.ok ? res.json() : { status: 'pending' }))
      .then((data: { status?: string }) => {
        if (!cancelled) {
          const status =
            data.status === 'signed' ? 'signed' : data.status === 'void' ? 'void' : 'pending';
          setContractStatus(status);
        }
      })
      .catch(() => {
        if (!cancelled) setContractStatus('pending');
      })
      .finally(() => {
        if (!cancelled) setContractStatusLoading(false);
      });
    return () => { cancelled = true; };
  }, [contractId]);

  // Prefer the PDF saved at generation so direct edits survive the signing page.
  useEffect(() => {
    if (!contractId) {
      setStoredPdfLookup('missing');
      return;
    }
    let cancelled = false;
    fetch(`${API_BASE}/api/contracts/by-contract-id/${encodeURIComponent(contractId)}/generated-pdf`)
      .then((res) => {
        if (!res.ok) throw new Error('missing');
        return res.arrayBuffer();
      })
      .then((buf) => {
        if (cancelled) return;
        setStoredGeneratedPdf(buf);
        setFilledPdfUrl(URL.createObjectURL(new Blob([buf], { type: 'application/pdf' })));
        setStoredPdfLookup('ready');
        setFillError(null);
      })
      .catch(() => {
        if (!cancelled) setStoredPdfLookup('missing');
      });
    return () => {
      cancelled = true;
    };
  }, [contractId]);

  // Load template PDF and fill with form data when no stored PDF is available.
  useEffect(() => {
    if (storedPdfLookup !== 'missing') return;
    if (!formData) return;
    const run = async () => {
      try {
        const { pdfDoc, pages, font: helveticaFont, pageAt } = await prepareContractPdf(pdfUrl, formData);
        const firstPage = pages[0];
        const placements = contractType === 'trial' ? TRIAL_TEXT_PLACEMENTS : TEXT_PLACEMENTS;

        const placeText = (key: string, value: string | undefined) => {
          const placement = placements[key];
          if (!placement || !('fontSize' in placement)) return;
          const text = String(value ?? '').trim();
          if (!text) return;
          const page = pageAt(placement.pageIndex ?? 0) ?? firstPage;
          page.drawText(text, {
            x: placement.x,
            y: placement.y,
            size: placement.fontSize ?? 10,
            font: helveticaFont,
            color: rgb(0, 0, 0),
          });
        };

        // Client/agreement info is shared by both contract types.
        placeText('effectiveDate', formData.effectiveDate);
        placeText('businessName', formData.businessName);
        placeText('serviceAddress', formData.serviceAddress);
        placeText('cityStateZip', formData.cityStateZip);
        placeText('locationContactNamePhone', formData.locationContactNamePhone);
        placeText('locationContactEmail', formData.locationContactEmail);
        placeText('authorizedPersonName', formData.authorizedPersonName);
        placeText('authorizedPersonTitle', formData.authorizedPersonTitle);
        placeText('authorizedPersonEmail', formData.authorizedPersonEmail);
        placeText('authorizedPersonPhone', formData.authorizedPersonPhone);
        placeText('clientName', formData.authorizedPersonName);
        placeText('clientPosition', formData.authorizedPersonTitle);

        if (contractType === 'trial') {
          // Trial: term dates + TIM-E + bins (-> Wheeled Bin) + bases (-> Base - Metal) + scope.
          // BIM-E has no line on the new template, so it is intentionally not drawn.
          placeText('agreementTermLength', formData.agreementTermLength);
          placeText('termStartDate', formData.termStartDate);
          placeText('qtyTimEBot', formData.qtyTimEBot);
          placeText('qtyBaseMetalMonthly', formData.qtyBasesTrial);
          placeText('qtyWheeledBinMonthly', formData.qtyBinsTrial);
          placeText('qtyKebbi', formData.qtyKebbi);
          if (formData.scopeText && formData.scopeText.trim()) {
            const scopePage = pageAt(TRIAL_SCOPE_PLACEMENT.pageIndex) ?? firstPage;
            drawTrialAgreementScopeText(scopePage, formData.scopeText, helveticaFont);
          }
        } else {
          placeText('agreementTermLength', formData.agreementTermLength);
          placeText('termStartDate', formData.termStartDate);
          placeText('implementationCost', formData.implementationCost);
          placeText('shippingFee', formData.shippingFee);
          placeText('qtyTimEBot', formData.qtyTimEBot);
          placeText('qtyTimECharger', formData.qtyTimECharger);
          placeText('qtyBaseMetalMonthly', formData.qtyBaseMetalMonthly);
          placeText('qtyInsulatedFoodTransportMonthly', formData.qtyInsulatedFoodTransportMonthly);
          placeText('qtyWheeledBinMonthly', formData.qtyWheeledBinMonthly);
          placeText('qtyUniversalPlatformMonthly', formData.qtyUniversalPlatformMonthly);
          placeText('qtyDoorOpenersMonthly', formData.qtyDoorOpenersMonthly);
          placeText('qtyNeuralTechBrainMonthly', formData.qtyNeuralTechBrainMonthly);
          placeText('qtyElevatorHardwareMonthly', formData.qtyElevatorHardwareMonthly);
          placeText('qtyLuggageCartMonthly', formData.qtyLuggageCartMonthly);
          placeText('qtyKebbi', formData.qtyKebbi);
          placeText('monthlyRoboticServiceCost', formData.monthlyRoboticServiceCost);
          // The one-time accessories have no reserved lines on the template;
          // they are itemised by the extras block on page 1.
          placeText('additionalAccessoriesCost', formData.additionalAccessoriesCost);
          placeText('implementationCostPage2', formData.implementationCost);
          placeText('totalMonthlyCost', formData.totalMonthlyCost);
          placeText('implementationCostDue', formData.implementationCostDue);
        }

        if (formData.techForceSignature) {
          const lastPage = pages[pages.length - 1];
          const placement = (contractType === 'trial' ? TRIAL_TEXT_PLACEMENTS : TEXT_PLACEMENTS).techforceSignature as { x: number; y: number; width?: number; height?: number; pageIndex?: number } | undefined;
          if (lastPage && placement && placement.width) {
            const imageBytes = await fetch(formData.techForceSignature).then((r) => r.arrayBuffer());
            const img = await pdfDoc.embedPng(imageBytes);
            const sigPage = placement.pageIndex !== undefined ? pageAt(placement.pageIndex) : lastPage;
            if (sigPage) sigPage.drawImage(img, { x: placement.x, y: placement.y, width: placement.width ?? 180, height: placement.height ?? 54 });
          }
        }

        const pdfBytes = await pdfDoc.save();
        const blobUrl = URL.createObjectURL(new Blob([new Uint8Array(pdfBytes)], { type: 'application/pdf' }));
        setFilledPdfUrl(blobUrl);
        setFillError(null);
      } catch (err) {
        console.error('Error filling PDF:', err);
        setFillError('Could not load or fill the contract PDF.');
      }
    };
    run();
  }, [formData, pdfUrl, contractType, storedPdfLookup]);

  useEffect(() => {
    return () => {
      if (filledPdfUrl && filledPdfUrl.startsWith('blob:')) URL.revokeObjectURL(filledPdfUrl);
    };
  }, [filledPdfUrl]);

  const generatePdfWithSignature = async (
    signatureImage: string,
    initials?: string,
    secondSignerFields?: { signature: string | null; name: string; position: string }
  ) => {
    try {
      setIsGeneratingPdf(true);
      if (!formData) {
        setIsGeneratingPdf(false);
        return null;
      }
      if (storedGeneratedPdf) {
        const pdfDoc = await PDFDocument.load(storedGeneratedPdf);
        const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
        const pages = pdfDoc.getPages();
        const insertedPages = Math.max(0, pages.length - 5);
        const pageAt = (templateIndex: number) => pages[shiftPageIndex(templateIndex, insertedPages)];
        const placements = contractType === 'trial' ? TRIAL_TEXT_PLACEMENTS : TEXT_PLACEMENTS;
        const lastPage = pages[pages.length - 1];
        const clientSigPlacement = placements.clientSignatureLastPage as { x: number; y: number; width?: number; height?: number; pageIndex?: number };

        const coverAndDraw = (key: string, value: string | undefined) => {
          const placement = placements[key] as { x: number; y: number; fontSize?: number; pageIndex?: number } | undefined;
          if (!placement) return;
          const page = pageAt(placement.pageIndex ?? 0);
          if (!page) return;
          const size = placement.fontSize ?? 10;
          page.drawRectangle({
            x: placement.x - 1,
            y: placement.y - 3,
            width: 220,
            height: size + 6,
            color: rgb(1, 1, 1),
          });
          const text = String(value ?? '').trim();
          if (!text) return;
          page.drawText(text, { x: placement.x, y: placement.y, size, font: helveticaFont, color: rgb(0, 0, 0) });
        };
        coverAndDraw('clientName', clientSignerName);
        coverAndDraw('clientPosition', clientSignerPosition);

        if (contractType !== 'trial') {
          const placeText = (key: string, value: string | undefined) => {
            const placement = placements[key] as { x: number; y: number; fontSize?: number; pageIndex?: number } | undefined;
            if (!placement) return;
            const text = String(value ?? '').trim();
            if (!text) return;
            const page = pageAt(placement.pageIndex ?? 0);
            if (!page) return;
            page.drawText(text, {
              x: placement.x,
              y: placement.y,
              size: placement.fontSize ?? 10,
              font: helveticaFont,
              color: rgb(0, 0, 0),
            });
          };
          placeText('billingEntity', billingEntity);
          const billingCityStateZipLine = [billingAddress, [billingCity, [billingState, billingZip].filter(Boolean).join(' ')].filter(Boolean).join(', ')].filter(Boolean).join(', ');
          placeText('billingCityStateZip', billingCityStateZipLine);
          placeText('billingContactNamePhone', `${billingContactName} ${billingContactPhone}`.trim());
          placeText('billingContactEmail', billingContactEmail);
        }

        const imageBytes = await fetch(signatureImage).then((res) => res.arrayBuffer());
        const signaturePdfImage = await pdfDoc.embedPng(imageBytes);
        const sigPage = (clientSigPlacement.pageIndex !== undefined ? pageAt(clientSigPlacement.pageIndex) : lastPage) ?? lastPage;
        if (sigPage) {
          sigPage.drawImage(signaturePdfImage, {
            x: clientSigPlacement.x,
            y: clientSigPlacement.y,
            width: clientSigPlacement.width ?? 180,
            height: clientSigPlacement.height ?? 54,
          });
        }
        if (secondSignerFields && requiresSecondSignature) {
          const secondPage = pageAt(SIGNATURE_PAGE_INDEX) ?? lastPage;
          if (secondPage) {
            if (secondSignerFields.signature) {
              const secondBytes = await fetch(secondSignerFields.signature).then((r) => r.arrayBuffer());
              const secondImg = await pdfDoc.embedPng(secondBytes);
              const p = SECOND_CLIENT_SIGNATURE_PLACEMENTS.signature;
              secondPage.drawImage(secondImg, { x: p.x, y: p.y, width: p.width, height: p.height });
            }
            const drawSecondField = (placement: { x: number; y: number; fontSize?: number }, value: string) => {
              const text = value.trim();
              if (!text) return;
              secondPage.drawText(text, {
                x: placement.x,
                y: placement.y,
                size: placement.fontSize ?? 10,
                font: helveticaFont,
                color: rgb(0, 0, 0),
              });
            };
            drawSecondField(SECOND_CLIENT_SIGNATURE_PLACEMENTS.name, secondSignerFields.name);
            drawSecondField(SECOND_CLIENT_SIGNATURE_PLACEMENTS.position, secondSignerFields.position);
          }
        }
        if (initials && initials.trim()) {
          const initialsPlacement = clientInitialsPlacement(pdfDoc);
          const page2 = pageAt(initialsPlacement.pageIndex ?? 1) ?? pages[1];
          if (page2) {
            page2.drawText(initials.trim(), {
              x: initialsPlacement.x,
              y: initialsPlacement.y,
              size: initialsPlacement.fontSize ?? 10,
              font: helveticaFont,
              color: rgb(0, 0, 0),
            });
          }
        }
        const pdfBytes = await pdfDoc.save();
        setIsGeneratingPdf(false);
        return URL.createObjectURL(new Blob([new Uint8Array(pdfBytes)], { type: 'application/pdf' }));
      }
      const { pdfDoc, pages, font: helveticaFont, pageAt } = await prepareContractPdf(pdfUrl, formData);
      const firstPage = pages[0];
      const lastPage = pages[pages.length - 1];
      const placements = contractType === 'trial' ? TRIAL_TEXT_PLACEMENTS : TEXT_PLACEMENTS;
      const clientSigPlacement = (contractType === 'trial' ? TRIAL_TEXT_PLACEMENTS : TEXT_PLACEMENTS).clientSignatureLastPage as { x: number; y: number; width?: number; height?: number; pageIndex?: number };

      if (formData) {
        const placeText = (key: string, value: string | undefined) => {
          const placement = placements[key];
          if (!placement || !('fontSize' in placement)) return;
          const text = String(value ?? '').trim();
          if (!text) return;
          const page = pageAt(placement.pageIndex ?? 0) ?? firstPage;
          page.drawText(text, {
            x: placement.x,
            y: placement.y,
            size: placement.fontSize ?? 10,
            font: helveticaFont,
            color: rgb(0, 0, 0),
          });
        };
        // Client/agreement info is shared by both contract types.
        placeText('effectiveDate', formData.effectiveDate);
        placeText('businessName', formData.businessName);
        placeText('serviceAddress', formData.serviceAddress);
        placeText('cityStateZip', formData.cityStateZip);
        placeText('locationContactNamePhone', formData.locationContactNamePhone);
        placeText('locationContactEmail', formData.locationContactEmail);
        placeText('authorizedPersonName', formData.authorizedPersonName);
        placeText('authorizedPersonTitle', formData.authorizedPersonTitle);
        placeText('authorizedPersonEmail', formData.authorizedPersonEmail);
        placeText('authorizedPersonPhone', formData.authorizedPersonPhone);
        // Authorization block — as typed by the client next to their signature,
        // not the team's prefill (which only seeds the input).
        placeText('clientName', clientSignerName);
        placeText('clientPosition', clientSignerPosition);

        if (contractType === 'trial') {
          // Trial: term dates + TIM-E + bins (-> Wheeled Bin) + bases (-> Base - Metal) + scope.
          // No billing collected; BIM-E has no line on the new template.
          placeText('agreementTermLength', formData.agreementTermLength);
          placeText('termStartDate', formData.termStartDate);
          placeText('qtyTimEBot', formData.qtyTimEBot);
          placeText('qtyBaseMetalMonthly', formData.qtyBasesTrial);
          placeText('qtyWheeledBinMonthly', formData.qtyBinsTrial);
          placeText('qtyKebbi', formData.qtyKebbi);
          if (formData.scopeText && formData.scopeText.trim()) {
            const scopePage = pageAt(TRIAL_SCOPE_PLACEMENT.pageIndex) ?? firstPage;
            drawTrialAgreementScopeText(scopePage, formData.scopeText, helveticaFont);
          }
        } else {
          placeText('billingEntity', billingEntity);
          const billingCityStateZipLine = [billingAddress, [billingCity, [billingState, billingZip].filter(Boolean).join(' ')].filter(Boolean).join(', ')].filter(Boolean).join(', ');
          placeText('billingCityStateZip', billingCityStateZipLine);
          placeText('billingContactNamePhone', `${billingContactName} ${billingContactPhone}`.trim());
          placeText('billingContactEmail', billingContactEmail);
          placeText('agreementTermLength', formData.agreementTermLength);
          placeText('termStartDate', formData.termStartDate);
          placeText('implementationCost', formData.implementationCost);
          placeText('shippingFee', formData.shippingFee);
          placeText('qtyTimEBot', formData.qtyTimEBot);
          placeText('qtyTimECharger', formData.qtyTimECharger);
          placeText('qtyBaseMetalMonthly', formData.qtyBaseMetalMonthly);
          placeText('qtyInsulatedFoodTransportMonthly', formData.qtyInsulatedFoodTransportMonthly);
          placeText('qtyWheeledBinMonthly', formData.qtyWheeledBinMonthly);
          placeText('qtyUniversalPlatformMonthly', formData.qtyUniversalPlatformMonthly);
          placeText('qtyDoorOpenersMonthly', formData.qtyDoorOpenersMonthly);
          placeText('qtyNeuralTechBrainMonthly', formData.qtyNeuralTechBrainMonthly);
          placeText('qtyElevatorHardwareMonthly', formData.qtyElevatorHardwareMonthly);
          placeText('qtyLuggageCartMonthly', formData.qtyLuggageCartMonthly);
          placeText('qtyKebbi', formData.qtyKebbi);
          placeText('monthlyRoboticServiceCost', formData.monthlyRoboticServiceCost);
          // The one-time accessories have no reserved lines on the template;
          // they are itemised by the extras block on page 1.
          placeText('additionalAccessoriesCost', formData.additionalAccessoriesCost);
          placeText('implementationCostPage2', formData.implementationCost);
          placeText('totalMonthlyCost', formData.totalMonthlyCost);
          placeText('implementationCostDue', formData.implementationCostDue);
        }
      }

      if (formData?.techForceSignature) {
        const placement = placements.techforceSignature as { x: number; y: number; width?: number; height?: number; pageIndex?: number } | undefined;
        if (placement && placement.width) {
          const techForceImageBytes = await fetch(formData.techForceSignature).then((r) => r.arrayBuffer());
          const techForceImg = await pdfDoc.embedPng(techForceImageBytes);
          const sigPage = placement.pageIndex !== undefined ? pageAt(placement.pageIndex) : lastPage;
          if (sigPage) sigPage.drawImage(techForceImg, { x: placement.x, y: placement.y, width: placement.width ?? 180, height: placement.height ?? 54 });
        }
      }

      const imageBytes = await fetch(signatureImage).then(res => res.arrayBuffer());
      const signaturePdfImage = await pdfDoc.embedPng(imageBytes);
      const sigPage =
        (clientSigPlacement.pageIndex !== undefined ? pageAt(clientSigPlacement.pageIndex) : lastPage) ?? lastPage;
      if (sigPage) sigPage.drawImage(signaturePdfImage, {
        x: clientSigPlacement.x,
        y: clientSigPlacement.y,
        width: clientSigPlacement.width ?? 180,
        height: clientSigPlacement.height ?? 54,
      });

      // Second signatory — the box itself was drawn by prepareContractPdf.
      if (secondSignerFields && requiresSecondSignature) {
        const secondPage = pageAt(SIGNATURE_PAGE_INDEX) ?? lastPage;
        if (secondPage) {
          if (secondSignerFields.signature) {
            const secondBytes = await fetch(secondSignerFields.signature).then((r) => r.arrayBuffer());
            const secondImg = await pdfDoc.embedPng(secondBytes);
            const p = SECOND_CLIENT_SIGNATURE_PLACEMENTS.signature;
            secondPage.drawImage(secondImg, { x: p.x, y: p.y, width: p.width, height: p.height });
          }
          const drawSecondField = (
            placement: { x: number; y: number; fontSize?: number },
            value: string
          ) => {
            const text = value.trim();
            if (!text) return;
            secondPage.drawText(text, {
              x: placement.x,
              y: placement.y,
              size: placement.fontSize ?? 10,
              font: helveticaFont,
              color: rgb(0, 0, 0),
            });
          };
          drawSecondField(SECOND_CLIENT_SIGNATURE_PLACEMENTS.name, secondSignerFields.name);
          drawSecondField(SECOND_CLIENT_SIGNATURE_PLACEMENTS.position, secondSignerFields.position);
        }
      }

      if (initials && initials.trim()) {
        const initialsPlacement = clientInitialsPlacement(pdfDoc);
        const page2 = pageAt(initialsPlacement.pageIndex ?? 1) ?? pages[1];
        if (page2) {
          page2.drawText(initials.trim(), {
            x: initialsPlacement.x,
            y: initialsPlacement.y,
            size: initialsPlacement.fontSize ?? 10,
            font: helveticaFont,
            color: rgb(0, 0, 0),
          });
        }
      }

      const pdfBytes = await pdfDoc.save();
      const url = URL.createObjectURL(new Blob([new Uint8Array(pdfBytes)], { type: 'application/pdf' }));
      setIsGeneratingPdf(false);
      return url;
    } catch (error) {
      console.error('Error generating PDF with signature:', error);
      setIsGeneratingPdf(false);
      return null;
    }
  };

  /** Freeze the drawn signature and refresh the preview so the client sees it in place. */
  const saveSignature = async () => {
    const dataURL = clientPad.capture();
    if (!dataURL) return;

    // Generate PDF with signature and initials (if entered) so preview shows initials on page 2
    const signedPdf = await generatePdfWithSignature(dataURL, clientInitials.trim() || undefined, {
      signature: secondPad.dataUrl,
      name: secondSignerName,
      position: secondSignerPosition,
    });
    if (signedPdf) {
      // Clean up previous blob URL if it exists
      if (signedPdfUrl && signedPdfUrl.startsWith('blob:')) {
        URL.revokeObjectURL(signedPdfUrl);
      }
      setSignedPdfUrl(signedPdf);
      alert('Signature saved! A new PDF with your signature has been generated.');
    } else {
      alert('Signature saved, but there was an error generating the PDF. Please try again.');
    }
  };

  /** Same as saveSignature, for the second signatory's pad. */
  const saveSecondSignature = async () => {
    const dataURL = secondPad.capture();
    if (!dataURL) return;
    if (!signature) {
      // Nothing to compose yet — the preview refreshes once the client signs.
      return;
    }
    const signedPdf = await generatePdfWithSignature(signature, clientInitials.trim() || undefined, {
      signature: dataURL,
      name: secondSignerName,
      position: secondSignerPosition,
    });
    if (signedPdf) {
      if (signedPdfUrl && signedPdfUrl.startsWith('blob:')) URL.revokeObjectURL(signedPdfUrl);
      setSignedPdfUrl(signedPdf);
    }
  };
  
  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (signedPdfUrl && signedPdfUrl.startsWith('blob:')) {
        URL.revokeObjectURL(signedPdfUrl);
      }
    };
  }, [signedPdfUrl]);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (formDataLoadState !== 'ready' || !formData) {
      alert('Contract is still loading. Please try again in a moment.');
      return;
    }
    if (!signature) {
      alert('Please sign the contract before submitting.');
      return;
    }
    const initials = (clientInitials || '').trim();
    if (contractType === 'service' && !initials) {
      alert('Please enter your initials to acknowledge the third-party electrician terms.');
      return;
    }
    if (!clientSignerName.trim() || !clientSignerPosition.trim()) {
      alert('Please enter the name and position of the person signing.');
      return;
    }
    const secondName = secondSignerName.trim();
    const secondPosition = secondSignerPosition.trim();
    if (requiresSecondSignature && (!secondPad.dataUrl || !secondName || !secondPosition)) {
      alert('This agreement requires a second signature. Please add the second signature, name, and position.');
      return;
    }
    if (!contractId) {
      alert('Invalid contract link.');
      return;
    }
    setIsSubmitting(true);
    try {
      // Regenerate PDF with signature + initials so submitted PDF has initials on bottom of page 2
      const url = await generatePdfWithSignature(signature, initials, {
        signature: secondPad.dataUrl,
        name: secondName,
        position: secondPosition,
      });
      if (!url) {
        alert('Could not generate the signed PDF. Please try again.');
        setIsSubmitting(false);
        return;
      }
      if (signedPdfUrl && signedPdfUrl.startsWith('blob:')) URL.revokeObjectURL(signedPdfUrl);
      const res = await fetch(url);
      const blob = await res.blob();
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const dataUrl = reader.result as string;
          resolve(dataUrl.split(',')[1] || '');
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      const patchRes = await fetch(`${API_BASE}/api/contracts/${encodeURIComponent(contractId)}/signed`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pdf_signed: base64,
          billingEntity: billingEntity?.trim() || undefined,
          billingAddress: billingAddress?.trim() || undefined,
          billingCity: billingCity?.trim() || undefined,
          billingState: billingState?.trim() || undefined,
          billingZip: billingZip?.trim() || undefined,
          billingContactName: billingContactName?.trim() || undefined,
          billingContactPhone: billingContactPhone?.trim() || undefined,
          billingContactEmail: billingContactEmail?.trim() || undefined,
          clientInitials: initials,
          clientName: clientSignerName.trim(),
          clientPosition: clientSignerPosition.trim(),
          clientSignatureSecond: requiresSecondSignature ? secondPad.dataUrl ?? undefined : undefined,
          clientSecondName: requiresSecondSignature ? secondName || undefined : undefined,
          clientSecondPosition: requiresSecondSignature ? secondPosition || undefined : undefined,
        }),
      });
      if (!patchRes.ok) {
        const err = await patchRes.json().catch(() => ({}));
        alert(err.error || 'Failed to save signed contract.');
        setIsSubmitting(false);
        return;
      }
      setContractStatus('signed');
    } catch (err) {
      console.error('Submit signed contract:', err);
      alert('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const displayPdfUrl = signedPdfUrl || filledPdfUrl || pdfUrl;
  const contractTitle = contractType === 'service'
    ? 'Robot Service Agreement'
    : 'Robot TRIAL Agreement';

  const isSubmitted = contractStatus === 'signed';
  // Voided contracts were retired before signing; the client should not be
  // presented with a form they cannot submit.
  const isVoided = contractStatus === 'void';

  return (
    <div className="page-container">

      <div className="contract-page">


        <div className="contract-header">
          <img src="/images/branding-logo.svg" alt="TechForce Robotics" className="contract-branding-logo" />
          <h2 className="contract-title">{contractTitle}</h2>
        </div>
      
        {contractStatusLoading ? (
          <div className="contract-thanks-section">
            <p className="contract-thanks-loading">Loading…</p>
          </div>
        ) : isSubmitted ? (
          <div className="contract-thanks-section">
            <h2 className="contract-thanks-title">Thank you</h2>
            <p className="contract-thanks-message">Thanks for submitting the information needed. Your contract has been received.</p>
          </div>
        ) : isVoided ? (
          <div className="contract-thanks-section">
            <h2 className="contract-thanks-title">This contract is no longer active</h2>
            <p className="contract-thanks-message">
              This agreement has been withdrawn and does not need to be signed. If you were expecting
              a contract, please contact your TechForce Robotics representative — a replacement may
              have been sent to you separately.
            </p>
          </div>
        ) : (
          <>
            <div className="page-content-container">

              <div className="contract-container">

                {/* PDF Viewer Section */}
                <div className="pdf-viewer-section">
                {fillError && (
                  <div className="contract-fill-error" role="alert">
                    {fillError}
                  </div>
                )}
                {formDataLoadState === 'loading' && !fillError && (
                  <p style={{ fontSize: '0.875rem', color: '#5A708D', marginBottom: '0.5rem' }}>
                    Loading contract…
                  </p>
                )}
                {isGeneratingPdf && (
                  <div style={{ textAlign: 'center', padding: '2rem', color: '#5A708D' }}>
                    <p>Generating PDF with your signature...</p>
                  </div>
                )}
                {!isGeneratingPdf && !fillError && formDataLoadState === 'ready' && formData && (
                  <>
                    <div className="pdf-container">
                      {/* <object> renders the PDF when the browser supports inline PDF view (Chrome with PDF Viewer, Safari, Firefox built-in). When the browser refuses (e.g. Chrome Incognito with the plugin disabled, most mobile browsers) it shows the fallback content below instead of the default broken-image icon. */}
                      <object
                        data={displayPdfUrl}
                        type="application/pdf"
                        className="pdf-iframe"
                        aria-label="Contract PDF preview"
                      >
                        <div className="pdf-fallback">
                          <p>Your browser can't show the contract preview here. Use the button below to open it in a new tab — you can still fill out and sign the contract on this page.</p>
                          <a
                            href={displayPdfUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="pdf-download-link"
                          >
                            Open Contract PDF
                          </a>
                        </div>
                      </object>
                    </div>
                    <div className="pdf-alternative-link">
                      {signedPdfUrl && (
                        <>
                          <a 
                            href={signedPdfUrl} 
                            download={`Signed-Contract-${contractId}.pdf`}
                            className="pdf-download-link"
                            style={{ marginRight: '0.5rem' }}
                          >
                            📥 Download Signed PDF
                          </a>
                          <a 
                            href={signedPdfUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="pdf-download-link"
                          >
                            📄 View Signed PDF
                          </a>
                        </>
                      )}
                      {!signedPdfUrl && (
                        <a 
                          href={displayPdfUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="pdf-download-link"
                        >
                          📄 Open PDF in new tab
                        </a>
                      )}
                      <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.875rem', color: '#5A708D', textAlign: 'center' }}>
                        {signedPdfUrl 
                          ? 'Your signature has been added to the PDF. Download or view the signed version above.'
                          : 'If the PDF doesn\'t display above, click the link to view it in a new tab'}
                      </p>
                    </div>
                  </>
                )}
                </div>

              </div>
              {/* end of contract section */}

            {/* Billing / acknowledgment / signature / submit — only render once the contract data is loaded so we never submit a signed PDF with no contract content. */}
            {formDataLoadState === 'ready' && formData && (
            <div className="contract-billing-container">

              {/* Billing Information — service contracts only. The trial flow keeps the lighter form and does not collect billing. */}
              {contractType === 'service' && (
              <div className="contract-billing-section">
                <h2 className="contract-billing-title">Billing Information</h2>
                <p className="contract-billing-description">Please provide billing details. This information will appear on the contract PDF when you submit.</p>
                <div className="contract-billing-grid">
                  <div className="contract-form-group">
                    <label className="form-label">Billing Entity</label>
                    <input
                      type="text"
                      className="form-input"
                      value={billingEntity}
                      onChange={(e) => setBillingEntity(e.target.value)}
                      placeholder="Company or entity name"
                    />
                  </div>
                  <div className="contract-form-group contract-form-group-full">
                    <label className="form-label">Billing Address</label>
                    <input
                      type="text"
                      className="form-input"
                      value={billingAddress}
                      onChange={(e) => setBillingAddress(e.target.value)}
                      placeholder="Street address"
                    />
                  </div>
                  <div className="contract-form-group">
                    <label className="form-label">City</label>
                    <input
                      type="text"
                      className="form-input"
                      value={billingCity}
                      onChange={(e) => setBillingCity(e.target.value)}
                      placeholder="City"
                    />
                  </div>
                  <div className="contract-form-group">
                    <label className="form-label">State</label>
                    <input
                      type="text"
                      className="form-input"
                      value={billingState}
                      onChange={(e) => setBillingState(e.target.value)}
                      placeholder="State"
                    />
                  </div>
                  <div className="contract-form-group">
                    <label className="form-label">Zip</label>
                    <input
                      type="text"
                      className="form-input"
                      value={billingZip}
                      onChange={(e) => setBillingZip(e.target.value)}
                      placeholder="Zip"
                    />
                  </div>
                  <div className="contract-form-group">
                    <label className="form-label">Billing Contact Name</label>
                    <input
                      type="text"
                      className="form-input"
                      value={billingContactName}
                      onChange={(e) => setBillingContactName(e.target.value)}
                      placeholder="Name"
                    />
                  </div>
                  <div className="contract-form-group">
                    <label className="form-label">Billing Contact Phone</label>
                    <input
                      type="tel"
                      className="form-input"
                      value={billingContactPhone}
                      onChange={(e) => setBillingContactPhone(e.target.value.replace(/[^\d\s\-().+]/g, ''))}
                      placeholder="Phone number"
                      inputMode="tel"
                      autoComplete="tel"
                    />
                  </div>
                  <div className="contract-form-group contract-form-group-full">
                    <label className="form-label">Billing Contact Email</label>
                    <input
                      type="email"
                      className="form-input"
                      value={billingContactEmail}
                      onChange={(e) => setBillingContactEmail(e.target.value)}
                      placeholder="Email"
                    />
                  </div>
                </div>
              </div>
              )}

              {/* Third-party electrician acknowledgment — service contracts only. */}
              {contractType === 'service' && (
              <div className="contract-acknowledgment-section">
                <h2 className="contract-acknowledgment-title">Third-party electrician terms</h2>
                <p className="contract-disclaimer-text">
                  Third-party electrician will alter all necessary access doors and any power requirements to power the robot(s). This will include a minimum automatic door opener for the robot(s)'s operation. All third-party costs to be paid by Client to their vendor. Cost may vary based upon needs. TechForce to manage and coordinate with third-party vendor at no cost to Client.
                </p>
                <p className="contract-initial-label">Initial to acknowledge.</p>
                <div className="contract-initial-input-group">
                  <label htmlFor="contract-initials" className="form-label">Your initials</label>
                  <input
                    id="contract-initials"
                    type="text"
                    className="form-input contract-initials-input"
                    value={clientInitials}
                    onChange={(e) => setClientInitials(e.target.value.replace(/[^a-zA-Z]/g, '').toUpperCase().slice(0, 8))}
                    placeholder="e.g. JC"
                    maxLength={8}
                    autoComplete="off"
                  />
                </div>
              </div>
              )}

              {/* Signature Section */}
              <div className="signature-section">
                <h2 className="signature-section-title">Sign Contract</h2>
                <p className="contract-billing-description">
                  Please sign below and provide the signer's name and position.
                </p>
                <div className="contract-billing-grid">
                  <div className="contract-form-group">
                    <label className="form-label">Name</label>
                    <input
                      type="text"
                      className="form-input"
                      value={clientSignerName}
                      onChange={(e) => setClientSignerName(e.target.value)}
                      placeholder="Full name"
                    />
                  </div>
                  <div className="contract-form-group">
                    <label className="form-label">Position</label>
                    <input
                      type="text"
                      className="form-input"
                      value={clientSignerPosition}
                      onChange={(e) => setClientSignerPosition(e.target.value)}
                      placeholder="Title or role"
                    />
                  </div>
                </div>
                <div className="signature-pad-container">
                  <canvas
                    ref={clientPad.canvasRef}
                    className="signature-canvas"
                    width={600}
                    height={200}
                    {...clientPad.handlers}
                  />
                  <div className="signature-actions">
                    <button
                      type="button"
                      className="clear-signature-button"
                      onClick={clientPad.clear}
                    >
                      Clear
                    </button>
                    <button
                      type="button"
                      className="save-signature-button"
                      onClick={saveSignature}
                    >
                      Save Signature
                    </button>
                  </div>
                  {signature && (
                    <div className="signature-preview">
                      <p className="signature-saved-message">✓ Signature saved</p>
                      <img src={signature} alt="Saved signature" className="signature-image" />
                    </div>
                  )}
                </div>
              </div>

              {/* Second signature — only when the agreement was generated requesting one. */}
              {requiresSecondSignature && (
                <div className="signature-section">
                  <h2 className="signature-section-title">Second Signature</h2>
                  <p className="contract-billing-description">
                    This agreement requires a second signature from your organization. Please sign below and
                    provide the signer's name and position.
                  </p>
                  <div className="contract-billing-grid">
                    <div className="contract-form-group">
                      <label className="form-label">Name</label>
                      <input
                        type="text"
                        className="form-input"
                        value={secondSignerName}
                        onChange={(e) => setSecondSignerName(e.target.value)}
                        placeholder="Full name"
                      />
                    </div>
                    <div className="contract-form-group">
                      <label className="form-label">Position</label>
                      <input
                        type="text"
                        className="form-input"
                        value={secondSignerPosition}
                        onChange={(e) => setSecondSignerPosition(e.target.value)}
                        placeholder="Title or role"
                      />
                    </div>
                  </div>
                  <div className="signature-pad-container">
                    <canvas
                      ref={secondPad.canvasRef}
                      className="signature-canvas"
                      width={600}
                      height={200}
                      {...secondPad.handlers}
                    />
                    <div className="signature-actions">
                      <button type="button" className="clear-signature-button" onClick={secondPad.clear}>
                        Clear
                      </button>
                      <button type="button" className="save-signature-button" onClick={saveSecondSignature}>
                        Save Signature
                      </button>
                    </div>
                    {secondPad.dataUrl && (
                      <div className="signature-preview">
                        <p className="signature-saved-message">✓ Second signature saved</p>
                        <img src={secondPad.dataUrl} alt="Saved second signature" className="signature-image" />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Submit Section */}
              <div className="contract-submit-section">
                <button
                  type="button"
                  className="submit-contract-button"
                  onClick={handleSubmit}
                  disabled={
                    !signature ||
                    !clientSignerName.trim() ||
                    !clientSignerPosition.trim() ||
                    (contractType === 'service' && !clientInitials.trim()) ||
                    (requiresSecondSignature &&
                      (!secondPad.dataUrl || !secondSignerName.trim() || !secondSignerPosition.trim())) ||
                    isSubmitting
                  }
                >
                  {isSubmitting ? 'Submitting…' : 'Submit Signed Contract'}
                </button>
              </div>

            </div>
            )}
            {/* end of billing information section */}

          </div>
        </>
      )}
      </div>
    </div>
  );
};

export default Contract;
